<template>
  <sui-modal
    name="barcodeScannerModal"
    :open="isModalOpen"
    v-on:clickAwayModal="clickAway"
  >
    <sui-modal-header>
      <div>Scan stregkode</div>
    </sui-modal-header>
    <sui-modal-content scrolling style="max-height: 70svh;">
      <p>
        Det anbefales at bruge vidvinkel kameraet på enheden til at scanne stregkoden for bedste resultat.
      </p>
      <sui-form-field style="padding-bottom: 5px;">
        <sui-dropdown 
          placeholder="Kamera"
          selection
          :options="cameraOptions"
          v-model="selectedDeviceId"
          style="width: 100%;"
        />
      </sui-form-field>

      <div class="zoom-control" style="padding: 10px 0;">
        <label style="display: flex; align-items: center; gap: 10px;">
          <i class="fas fa-search-minus" style="min-width: 20px;"></i>
          <input 
            type="range" 
            :min="1" 
            :max="4"
            :step="0.1"
            v-model.number="currentZoom"
            style="flex: 1;"
          />
          <i class="fas fa-search-plus" style="min-width: 20px;"></i>
          <span style="min-width: 3ch;">{{ currentZoom.toFixed(1) }}x</span>
        </label>
      </div>

      <div class="mt-3" v-if="resultIsFaulty">
        <p style="color: red;">Serienummeret er ikke gyldigt!</p>
      </div>
      <div style="position: relative; display: inline-block; width: 100%; max-width: 100%;">
        <video
          ref="videoEl"
          style="display: none;"
          playsinline
          muted
          autoplay
        />
        <canvas
          ref="displayCanvas"
          style="border: 1px solid gray; display: block; width: 100%; max-width: 100%; height: auto;"
        />
        <canvas
          ref="canvasEl"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none;"
        />
      </div>

      <!-- <div class="mt-3">
        <label>Result:</label>
        <pre><code id="result">{{ resultText }}</code></pre>
      </div> -->
    

    </sui-modal-content>
    <sui-modal-actions>
      <sui-button @click="start()" :disabled="scanning">Start scanning</sui-button>
      <sui-button @click="reset()" :disabled="!scanning">Stop scanning</sui-button>
    </sui-modal-actions>
  </sui-modal>
</template>

<script>
import EventBus from '@/EventBus'
import swal from 'sweetalert';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { DataAPI } from '../../lib/DataAPI';

export default {
  name: 'BarcodeScannerModal',

  mixins: [DataAPI],

  // enums: {},

  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      // Data properties go here
      videoEl: null,
      canvasEl: null,
      displayCanvas: null,
      devices: [],
      selectedDeviceId: undefined,
      resultText: '',
      scanning: false,
      resultIsFaulty: false,
      defaultCamera: null,

      // Zoom properties
      currentZoom: 1,
      animationFrameId: null,
      scanIntervalId: null,
      processingResult: false, // Flag to prevent multiple scans during result processing

      codeReader: new BrowserMultiFormatReader(),
    }
  },

  computed: {
    // Computed properties go here
    cameraOptions() {
      const options = [];
      for (const d of this.devices) {
      options.push({
        text: d.label || `Camera ${d.deviceId.slice(0, 6)}`,
        value: d.deviceId
      });
      }
      return options;
    }
  },

  mounted() {
    this.videoEl = this.$refs.videoEl;
    this.canvasEl = this.$refs.canvasEl;
    this.displayCanvas = this.$refs.displayCanvas;
    
    // Restore saved zoom level
    const savedZoom = window.localStorage.getItem('barcode-scanner-zoom-level');
    if (savedZoom) {
      const zoomValue = parseFloat(savedZoom);
      if (!isNaN(zoomValue) && zoomValue >= 1 && zoomValue <= 4) {
        this.currentZoom = zoomValue;
      }
    }
  },

  beforeDestroy() {
    this.reset()
    this.selectedDeviceId = undefined
  },

  methods: {
    clickAway() {
      this.reset();
      this.selectedDeviceId = undefined
      EventBus.$emit('barcode-scanner-modal-closing')
    },

    drawOverlay() {
      const video = this.videoEl
      const canvas = this.canvasEl
      if (!video || !canvas) return

      canvas.width = this.displayCanvas.width
      canvas.height = this.displayCanvas.height

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      ctx.fillStyle = 'rgba(0, 0, 0, 0.60)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const rectWidth = canvas.width
      const rectHeight = canvas.height * 0.4
      const rectX = (canvas.width - rectWidth) / 2
      const rectY = (canvas.height - rectHeight) / 2
      ctx.clearRect(rectX, rectY, rectWidth, rectHeight)

      // Use Tailwind color (e.g., 'green-500', 'primary', etc.)
      ctx.strokeStyle = '#D69D56'
      ctx.lineWidth = 2
      ctx.strokeRect(rectX, rectY, rectWidth, rectHeight)
    },

    drawZoomedFrame() {
      if (!this.scanning || !this.videoEl || !this.displayCanvas) return;
      
      const video = this.videoEl;
      const canvas = this.displayCanvas;
      const ctx = canvas.getContext('2d');
      
      if (!ctx || video.readyState < video.HAVE_CURRENT_DATA) {
        this.animationFrameId = requestAnimationFrame(() => this.drawZoomedFrame());
        return;
      }
      
      // Set canvas size to match video dimensions (only once)
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Apply zoom transformation
      const zoom = this.currentZoom;
      
      if (zoom === 1) {
        // No zoom - just draw the video directly
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      } else {
        // Zoom in - draw center portion of video to fill canvas
        // Calculate the source rectangle (what portion of video to capture)
        const sourceWidth = video.videoWidth / zoom;
        const sourceHeight = video.videoHeight / zoom;
        const sourceX = (video.videoWidth - sourceWidth) / 2;
        const sourceY = (video.videoHeight - sourceHeight) / 2;
        
        // Draw the source rectangle to fill the entire canvas
        ctx.drawImage(
          video,
          sourceX, sourceY, sourceWidth, sourceHeight,  // source rectangle
          0, 0, canvas.width, canvas.height              // destination rectangle
        );
      }
      
      // Continue rendering
      this.animationFrameId = requestAnimationFrame(() => this.drawZoomedFrame());
    },

    async scanFromCanvas() {
      // Early return if already processing a result
      if (this.processingResult || !this.scanning || !this.displayCanvas || !this.scanIntervalId) return;
      
      try {
        // Convert canvas to data URL and create an image element
        const dataUrl = this.displayCanvas.toDataURL('image/png');
        const img = new Image();
        img.src = dataUrl;
        
        // Wait for image to load
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        
        // Double-check we're still scanning before processing
        if (!this.scanning || this.processingResult) return;
        
        const result = await this.codeReader.decodeFromImageElement(img);
        if (result) {
          // Triple-check we're still actually scanning and haven't closed
          if (!this.scanning || this.processingResult) return;
          
          // Set processing flag immediately to prevent any more scans
          this.processingResult = true;
          this.resultText = result.getText();
          
          // Stop scanning immediately to prevent further scans while processing
          if (this.scanIntervalId) {
            clearInterval(this.scanIntervalId);
            this.scanIntervalId = null;
          }
        }
      } catch (err) {
        // NotFoundException is expected when no barcode is visible
        if (!(err instanceof NotFoundException)) {
          console.error('Scan error:', err);
        }
      }
    },

    async loadDevices() {
      try {
        try { 
          await navigator.mediaDevices.getUserMedia({ video: true }) 
        } catch {
          console.warn('Camera permission denied.')
          await this.reset();
          return
        }
        const list = await this.codeReader.listVideoInputDevices()
        this.devices = list

        if (!list || list.length === 0) {
          console.warn('No video device selected for barcode scanner.')
          swal("Fejl", "Ingen kamera tilgængeligt.", "error")
          return
        }

        if (!this.selectedDeviceId && list && list.length > 0) {
          const first = list[0]
          if (first && first.deviceId) {
            this.selectedDeviceId = first.deviceId
          }
        }
      } catch (err) {
        console.error('Failed to list video devices', err)
      }
    },

    stopStream() {
      const stream = this.videoEl?.srcObject;
      if (stream) stream.getTracks().forEach(t => t.stop())
      if (this.videoEl) {
        this.videoEl.srcObject = null;
      }
    },

    start() {
      setTimeout(() => {
        console.log('Starting barcode scanner...', this.selectedDeviceId, this.videoEl, this.scanning);
        if (!this.videoEl || this.scanning) return
        this.codeReader.reset()
        this.stopStream()
        this.resultText = ''
        this.processingResult = false // Reset processing flag

        const selectedId = this.selectedDeviceId ?? null;
        console.log('Using selected device ID:', selectedId);

        if (!selectedId) {
          return
        }

        try {
          // Start video stream manually
          navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: selectedId } }
          }).then(stream => {
            this.videoEl.srcObject = stream;
            this.scanning = true;
            
            // Wait for video to be ready
            this.videoEl.onloadedmetadata = () => {
              this.videoEl.play().then(() => {
                
                setTimeout(() => {
                  // Start canvas rendering loop
                  this.drawZoomedFrame();
                  
                  // Start barcode scanning at intervals
                  this.scanIntervalId = setInterval(() => {
                    this.scanFromCanvas();
                  }, 100); // Scan every 100ms
                  
                  // Draw overlay
                  setTimeout(() => {
                    this.drawOverlay();
                  }, 250);
                }, 125);
              }).catch(playErr => {
                console.error('Failed to play video:', playErr);
              });
            };
          }).catch(err => {
            console.error('Failed to start camera:', err);
            this.reset();
          });

          console.log(`Started continuous decode from camera with id ${this.selectedDeviceId ?? 'default'}`)
        } catch (e) {
          console.error('Failed to start scanner', e)
        }  
      }, 100);
    },

    async reset() {
      console.log('Resetting barcode scanner...')
      
      // Stop scanning FIRST to prevent any in-flight async operations from completing
      this.scanning = false;
      
      // Set processing flag to stop any in-flight scans
      this.processingResult = false;
      
      // Stop animation frame
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Stop scan interval
      if (this.scanIntervalId) {
        clearInterval(this.scanIntervalId);
        this.scanIntervalId = null;
      }
      
      this.codeReader.reset()
      this.stopStream()
      this.resultText = ''

      // Clear canvases
      const canvas = this.canvasEl
      if (canvas) {
        const ctx = canvas.getContext('2d')
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      
      const displayCanvas = this.displayCanvas
      if (displayCanvas) {
        const ctx = displayCanvas.getContext('2d')
        if (ctx) ctx.clearRect(0, 0, displayCanvas.width, displayCanvas.height)
      }

      await new Promise(resolve => setTimeout(resolve, 200))
    },

  },

  watch: {
    isModalOpen: {
      immediate: false,
      handler(isOpen) {
        if (isOpen) {
          this.loadDevices().then(() => {
            this.start();

            // TODO: Load default camera from settings
            this.defaultCamera = window.localStorage.getItem('default-camera-for-barcode-scanner');
            console.log('Default camera from localStorage:', this.defaultCamera);

            setTimeout(() => {
              console.log('Checking for default camera to select...', this.defaultCamera, this.devices);
              if (this.defaultCamera) {
                const exists = this.devices.find(d => d.deviceId === this.defaultCamera);
                if (exists && this.defaultCamera !== this.selectedDeviceId) {
                  console.log('Setting selected device to default camera from localStorage:', this.defaultCamera);
                  this.selectedDeviceId = this.defaultCamera; 
                }
              }
            }, 500);
          })
        } 
      }
    },

    selectedDeviceId: {
      immediate: false,
      handler() {
        if (this.scanning) {
          this.reset().then(() => {
            this.start();
            window.localStorage.setItem('default-camera-for-barcode-scanner', String(this.selectedDeviceId) || '');
          })
        }
      }
    },

    currentZoom: {
      immediate: false,
      handler(newZoom) {
        // Save zoom preference
        window.localStorage.setItem('barcode-scanner-zoom-level', String(newZoom));
      }
    },

    resultText: {
      immediate: false,
      handler(newValue) {
        if (newValue) {
          this.resultIsFaulty = false;
          // TODO: Do some validation on the scanned data
          if (!/^(ALCL|ICTR|CIGG|GNXS).{8}$/.test(newValue)) { //Anything not matching this regex will result in error 400 from the create/update API endpoint
            console.log('Scanned barcode data is invalid:', newValue);
            this.resultText = '';
            this.resultIsFaulty = true;
            this.processingResult = false; // Reset flag for invalid results
            return;
          }

          this.dataPonboxIsWhitelisted(newValue).then(response => {
            if (!response) {
              console.log('Scanned barcode data is not whitelisted:', newValue);
              this.resultText = '';
              this.resultIsFaulty = true;
              this.processingResult = false; // Reset flag
              return;
            }
            console.log('Scanned barcode data is whitelisted:', newValue, response);
            if (response.isWhiteListed) {
              // Stop all scanning activity FIRST before emitting result
              this.scanning = false; // Stop scanning immediately to prevent scanFromCanvas from continuing
              
              if (this.scanIntervalId) {
                clearInterval(this.scanIntervalId);
                this.scanIntervalId = null;
              }
              if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
              }
              
              // Wait a tick to ensure no scan callbacks are in progress
              this.$nextTick(() => {
                // Emit the event and let parent component close modal
                EventBus.$emit('scanned-barcode-data', response);
                
                // Give parent component time to process before cleanup
                setTimeout(() => {
                  this.clickAway();
                }, 150);
              });
            } else {
              this.resultText = '';
              this.resultIsFaulty = true;
              this.processingResult = false; // Reset flag
              return;
            }
          }).catch(err => {
            console.error('Error checking if scanned barcode data is whitelisted:', err);
            this.resultText = '';
            this.resultIsFaulty = true;
            this.processingResult = false; // Reset flag
            return;
          });
        }
      }
    },

    cameraOptions: {
      immediate: false,
      handler(newOptions) {
        console.log('Camera options updated:', newOptions);
        // settingsManager.setSettingOptions('defaultCameraForBarcodeScanner', newOptions);
        // // If default camera setting is set, try to select it
        // if (defaultCameraSetting.value) {
        //   const exists = newOptions.find(opt => opt.value === defaultCameraSetting.value);
        //   if (exists) {
        //     console.log('Setting selected device to default camera setting:', defaultCameraSetting.value);
        //     selectedDeviceId.value = defaultCameraSetting.value;
        //   }
        // }
      }
    },
  },
}


</script>